import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Snackbar, CircularProgress } from "@mui/material";
import { exportToAgromanager } from "../../../../../../API/result_set_layer";
import Button from "../../../../../../Component/Button/Button";
import { MessageLevel } from "../../../../../../Component/message-flash/MessageFlash";

function ExportToAgroBtn({
  resultSetId,
}: {
  resultSetId: string;
}): React.ReactNode {
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(false);
  const [notification, setNotification] = useState<{
    level: MessageLevel;
    message: string;
  } | null>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isDisabled) {
      timer = setTimeout(() => {
        setIsDisabled(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [isDisabled]);

  const handleExport = async () => {
    if (isDisabled) return;

    setIsDisabled(true);

    try {
      const response = await exportToAgromanager(resultSetId);

      if (response && response.ok) {
        setNotification({
          level: MessageLevel.Success,
          message: t("flash.export_resultset_to_agromanager_success"),
        });
      } else {
        setNotification({
          level: MessageLevel.Error,
          message: t("flash.export_resultset_to_agromanager_error"),
        });
      }
    } catch {
      setNotification({
        level: MessageLevel.Error,
        message: t("flash.export_resultset_to_agromanager_error"),
      });
    }
  };

  const handleClose = () => setNotification(null);

  return (
    <>
      <Button
        variant="outlined"
        disableElevation
        fullWidth
        size="large"
        onClick={handleExport}
        disabled={isDisabled}
        startIcon={
          isDisabled ? <CircularProgress size={20} color="inherit" /> : null
        }
      >
        {isDisabled
          ? t("button.processing")
          : t("button.export_resultset_to_agromanager")}
      </Button>

      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {notification ? (
          <Alert
            onClose={handleClose}
            severity={notification.level}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default ExportToAgroBtn;
